import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { hasComplianceReviewRole } from '@/utils/roleHelpers';
import { env } from '@/config/env';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import { useComplianceReviewAccess } from '@/hooks/useComplianceReviewAccess';

interface ReviewerRouteProps {
  children: React.ReactElement;
}

/**
 * Protected route wrapper for the compliance review dashboard.
 * Allows Administrators and Users in an approving org or ProductAndEngineering
 * (see useComplianceReviewAccess). Redirects all other users to dashboard.
 */
const ReviewerRoute: React.FC<ReviewerRouteProps> = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { canAccessComplianceReview, isCheckingComplianceReviewAccess } = useComplianceReviewAccess();

  // Bypass guard when authentication is disabled (development mode)
  if (!env.authEnabled && !env.authorizationEnabled) {
    return children;
  }

  // If not authenticated, redirect to login (handled by AuthGuard)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role gate first; the approving-org check below needs an async lookup.
  if (!user || !hasComplianceReviewRole(user.roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isCheckingComplianceReviewAccess) {
    return <LoadingSpinner />;
  }

  if (!canAccessComplianceReview) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ReviewerRoute;
